import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaAngleLeft,
  FaAngleRight,
  FaBars,
  FaUsers,
  FaClipboardList,
  FaRegFileAlt,
  FaRegChartBar,
  FaUserCog,
  FaTimes,
  FaFileExport,
  FaMoneyBillAlt,
  FaStore,
  FaFileExcel,
  FaTshirt,
  FaFileSignature,
} from 'react-icons/fa';
import logo from '../assets/images/login-logo.png';

const ICON_MAP = {
  dashboard: FaRegChartBar,
  employees: FaUsers,
  settings: FaUserCog,
  payslip: FaMoneyBillAlt,
  files: FaRegFileAlt,
  fileExcel: FaFileExcel,
  salaryStatement: FaFileExcel,
  offerLetter: FaFileSignature,
  uniformDress: FaTshirt,
  uniformForms: FaTshirt,
  attendance: FaClipboardList,
  reports: FaFileExport,
  chart: FaRegChartBar,
  store: FaStore,
};

const normalizePath = (path = '') => {
  if (!path) {
    return '';
  }
  return path.startsWith('/') ? path : `/${path}`;
};

const Sidebar = ({ collapsed, toggleSidebar, menus = [], isMobileOpen = false, onCloseMobile }) => {
  const location = useLocation();

  const items = useMemo(() => {
    if (!Array.isArray(menus)) {
      return [];
    }

    return menus
      .filter((menu) => menu && menu.path)
      .map((menu) => {
        const normalizedPath = normalizePath(menu.path.trim());
        const IconComponent = ICON_MAP[menu.icon] ?? FaBars;

        return {
          key: menu.id ?? normalizedPath,
          path: normalizedPath,
          title: menu.title ?? '',
          IconComponent,
          sortOrder:
            typeof menu.sortOrder === 'number' ? menu.sortOrder : Number.MAX_SAFE_INTEGER,
        };
      })
      .sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) {
          return a.sortOrder - b.sortOrder;
        }
        return a.title.localeCompare(b.title);
      });
  }, [menus]);

  const sidebarClassName = [
    'sidebar-container',
    'text-white',
    collapsed ? 'sidebar-collapsed' : 'sidebar-expanded',
    isMobileOpen ? 'sidebar-mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleCloseMobile = () => {
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  return (
    <div className={sidebarClassName}>
      <div className="p-3 border-bottom sidebar-header-border">
        <div className="d-flex align-items-center justify-content-between">
          {!collapsed && (
            <div className="text-center flex-grow-1">
              <img src={logo} alt="Logo" style={{ width: '80px', height: '80px' }} />
            </div>
          )}
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-link text-white p-0 d-md-none"
              type="button"
              onClick={handleCloseMobile}
              aria-label="Close navigation"
            >
              <FaTimes size={20} />
            </button>
            <button
              className="btn btn-link text-white p-0 d-none d-md-inline-flex"
              onClick={toggleSidebar}
              type="button"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <FaAngleRight /> : <FaAngleLeft />}
            </button>
          </div>
        </div>
      </div>
      <nav className="nav flex-column py-2">
        {items.length === 0 ? (
          <div className="px-4 py-3 text-white-50 small">
            No menus assigned to your role.
          </div>
        ) : (
          items.map((item) => {
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

            return (
              <Link
                key={item.key}
                to={item.path}
                className={`nav-link text-white py-2 ${collapsed ? 'justify-content-center' : 'ps-4'
                  } ${isActive ? 'active-menu' : ''}`}
                onClick={handleCloseMobile}
              >
                <item.IconComponent className={collapsed ? '' : 'me-3'} />
                {!collapsed && item.title}
              </Link>
            );
          })
        )}
      </nav>
    </div>
  );
};

export default Sidebar;